import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../../App";
import { toast } from "sonner";
import {
  ChevronLeft, Save, Plus, Minus,
  Sparkles, Film, Image, Globe, Layout,
  Palette, Presentation, BookOpen, RectangleHorizontal,
  Table2, HelpCircle, Zap, FileText, X, Printer
} from "lucide-react";

const fmt = (n) => "$" + Math.round(n).toLocaleString();

const DEFAULT_PRICES = {
  landing_page: 3000,
  full_website: 7500,
  branding: 10000,
  investment_presentation: 4000,
  flipbook: 7500,
  billboard_static: 1500,
  billboard_animated: 3000,
  unit_inventory: 750,
  ai_animated: 899,
  ai_enhance: 599,
  ai_retainer_6: 5394,
  ai_retainer_12: 9708,
  ai_retainer_18: 13755,
};

const buildInfraCategories = (p) => [
  {
    id: "digital", label: "Digital Presence", color: "sky",
    items: [
      { id: "landing_page", label: "Landing Page", price: p.landing_page, icon: Layout, type: "toggle", excludes: "full_website",
        desc: "Single high-converting page designed for lead capture and campaign traffic." },
      { id: "full_website", label: "Full Website", price: p.full_website, icon: Globe, type: "toggle", excludes: "landing_page",
        desc: "Multi-page website with full structure, content, and user journey for the development." },
    ]
  },
  {
    id: "branding", label: "Branding & Positioning", color: "violet",
    items: [
      { id: "branding", label: "Branding & Conceptual Package", price: p.branding, icon: Palette, type: "toggle",
        desc: "A complete identity system — positioning, name and messaging, logo, brand deck, one sheet, and location map — delivered as production-ready files." },
      { id: "investment_presentation", label: "Investment Presentation", price: p.investment_presentation, icon: Presentation, type: "toggle",
        subtext: "Up to 20 pages",
        desc: "Investor-facing presentation designed to communicate the vision, opportunity, and financial narrative." },
    ]
  },
  {
    id: "collateral", label: "Sales & Marketing Collateral", color: "amber",
    items: [
      { id: "flipbook", label: "Development Flipbook", price: p.flipbook, icon: BookOpen, type: "toggle",
        subtext: "40-50 pages",
        desc: "Comprehensive project sales and marketing book used for presentations, brokers, and buyers." },
      { id: "billboard", label: "Billboard Design Creation", icon: RectangleHorizontal, type: "dropdown",
        desc: "Billboard-ready creative using project visuals for awareness and traffic.",
        options: [
          { value: "none", label: "None", price: 0 },
          { value: "static", label: "Static", price: p.billboard_static },
          { value: "animated", label: "Animated (up to 10 sec)", price: p.billboard_animated },
        ]},
      { id: "unit_inventory", label: "Unit Inventory Breakdown Sheet", price: p.unit_inventory, icon: Table2, type: "toggle",
        desc: "Clean, structured unit matrix for sales teams and buyers." },
    ]
  },
  {
    id: "ai_content", label: "AI-Enhanced Marketing Content", color: "rose",
    items: [
      { id: "ai_social", label: "AI Social Media Content", icon: Film, type: "retainer",
        subtext: "15-second video clips — AI-assisted, no revisions included",
        desc: "Short-form animated clips generated from existing renderings for use in ads and social media. Monthly retainer packages.",
        tiers: [
          { value: "none", label: "None", clips: 0, monthly: 0 },
          { value: "6", label: "6 clips/mo", clips: 6, monthly: p.ai_retainer_6 },
          { value: "12", label: "12 clips/mo", clips: 12, monthly: p.ai_retainer_12 },
          { value: "18", label: "18 clips/mo", clips: 18, monthly: p.ai_retainer_18 },
        ]},
      { id: "ai_enhance", label: "Advanced Render Enhancement", price: p.ai_enhance, icon: Image, type: "enhancement_block", defaultQty: 3,
        subtitle: "AI-Driven Enhancement Pipeline + Precision Photoshop + Cinematic Post-Production",
        desc: "Each image undergoes a multi-step enhancement process combining advanced AI upscaling, material refinement, lighting adjustments, and detailed Photoshop post-production.",
        process: [
          "AI-based super resolution & detail reconstruction",
          "Texture refinement & material clarity adjustments",
          "Lighting balance & dynamic range optimization",
          "Atmospheric depth & environmental enhancements",
          "Edge sharpening & artifact cleanup",
          "Final Photoshop polish and color grading"
        ],
        disclaimer: "Delivered as final enhanced output — no revision rounds included." },
    ]
  }
];

function Toggle({ enabled, onChange, testId }) {
  return (
    <button onClick={() => onChange(!enabled)}
      className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${enabled ? "bg-white" : "bg-white/10"}`}
      data-testid={testId}>
      <div className={`absolute top-0.5 w-4 h-4 rounded-full transition-transform ${enabled ? "bg-black translate-x-4" : "bg-white translate-x-0.5"}`} />
    </button>
  );
}

function SummaryRow({ label, value, testId, highlight }) {
  return (
    <div className="flex items-center justify-between py-3" data-testid={testId}>
      <span className={`text-sm ${highlight ? "text-white/70 font-medium" : "text-white/50"}`}>{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${highlight ? "text-white" : "text-white/80"}`}>{value}</span>
    </div>
  );
}

export default function MarketingCollateralCalculator() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = quoteId === "new";

  const [prices, setPrices] = useState(DEFAULT_PRICES);
  const [infraToggles, setInfraToggles] = useState({
    landing_page: false, full_website: false,
    branding: false, investment_presentation: false,
    flipbook: false, unit_inventory: false,
    ai_enhance: false,
  });
  const [billboardOption, setBillboardOption] = useState("none");
  const [aiRetainerTier, setAiRetainerTier] = useState("none");
  const [aiEnhanceQty, setAiEnhanceQty] = useState(3);
  const [retainerMonths, setRetainerMonths] = useState(6);
  const [activeTooltip, setActiveTooltip] = useState(null);

  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [saving, setSaving] = useState(false);
  const [existingQuoteId, setExistingQuoteId] = useState(null);
  const [proposalHtml, setProposalHtml] = useState(null);
  const [generatingProposal, setGeneratingProposal] = useState(false);

  const handleInfraToggle = (id, val) => {
    const cats = buildInfraCategories(prices);
    const item = cats.flatMap(c => c.items).find(i => i.id === id);
    const next = { ...infraToggles, [id]: val };
    if (val && item?.excludes) next[item.excludes] = false;
    setInfraToggles(next);
  };

  useEffect(() => {
    apiClient.get("/calculators/marketing-pricing").then(res => {
      if (res.data && Object.keys(res.data).length > 0) setPrices(prev => ({ ...prev, ...res.data }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isNew && quoteId) {
      apiClient.get(`/calculators/quotes/${quoteId}`).then(res => {
        const q = res.data;
        if (q.metadata?.collateral) {
          const m = q.metadata.collateral;
          if (m.infra_toggles) setInfraToggles(prev => ({ ...prev, ...m.infra_toggles }));
          if (m.billboard_option) setBillboardOption(m.billboard_option);
          if (m.ai_retainer_tier) setAiRetainerTier(m.ai_retainer_tier);
          if (m.ai_enhance_qty) setAiEnhanceQty(m.ai_enhance_qty);
          if (m.retainer_months) setRetainerMonths(m.retainer_months);
        }
        setProjectName(q.project_name || "");
        setClientName(q.client_name || "");
        setExistingQuoteId(q.id);
      }).catch(() => toast.error("Failed to load quote"));
    }
  }, [quoteId, isNew]);

  const infraCategories = useMemo(() => buildInfraCategories(prices), [prices]);

  const calc = useMemo(() => {
    let oneTimeTotal = 0;
    const items = [];

    if (infraToggles.landing_page) { oneTimeTotal += prices.landing_page; items.push({ label: "Landing Page", amount: prices.landing_page }); }
    if (infraToggles.full_website) { oneTimeTotal += prices.full_website; items.push({ label: "Full Website", amount: prices.full_website }); }
    if (infraToggles.branding) { oneTimeTotal += prices.branding; items.push({ label: "Branding & Conceptual Package", amount: prices.branding }); }
    if (infraToggles.investment_presentation) { oneTimeTotal += prices.investment_presentation; items.push({ label: "Investment Presentation", amount: prices.investment_presentation }); }
    if (infraToggles.flipbook) { oneTimeTotal += prices.flipbook; items.push({ label: "Development Flipbook", amount: prices.flipbook }); }

    const billboardPrice = billboardOption === "static" ? prices.billboard_static : billboardOption === "animated" ? prices.billboard_animated : 0;
    if (billboardPrice > 0) { oneTimeTotal += billboardPrice; items.push({ label: `Billboard Design (${billboardOption === "static" ? "Static" : "Animated"})`, amount: billboardPrice }); }

    if (infraToggles.unit_inventory) { oneTimeTotal += prices.unit_inventory; items.push({ label: "Unit Inventory Breakdown Sheet", amount: prices.unit_inventory }); }

    const aiEnhanceTotal = infraToggles.ai_enhance ? aiEnhanceQty * prices.ai_enhance : 0;
    if (aiEnhanceTotal > 0) { oneTimeTotal += aiEnhanceTotal; items.push({ label: `Advanced Render Enhancement (${aiEnhanceQty} images)`, amount: aiEnhanceTotal }); }

    const retainerTierPrices = { "6": prices.ai_retainer_6, "12": prices.ai_retainer_12, "18": prices.ai_retainer_18 };
    const aiRetainerMonthly = aiRetainerTier !== "none" ? (retainerTierPrices[aiRetainerTier] || 0) : 0;
    const aiRetainerTotal = aiRetainerMonthly * retainerMonths;

    const grandTotal = oneTimeTotal + aiRetainerTotal;

    return { oneTimeTotal, items, aiEnhanceTotal, aiRetainerMonthly, aiRetainerTotal, grandTotal, retainerMonths };
  }, [prices, infraToggles, billboardOption, aiRetainerTier, aiEnhanceQty, retainerMonths]);

  const handleSave = async () => {
    if (!projectName.trim()) { toast.error("Enter a project name"); return; }
    setSaving(true);
    try {
      const lineItems = calc.items.map(item => ({
        id: crypto.randomUUID(), label: item.label, quantity: 1, rate: item.amount, subtotal: item.amount, unit_type: "one-time"
      }));
      if (calc.aiRetainerMonthly > 0) {
        lineItems.push({
          id: crypto.randomUUID(), label: `AI Social Media Content (${aiRetainerTier} clips/mo)`,
          quantity: retainerMonths, rate: calc.aiRetainerMonthly, subtotal: calc.aiRetainerTotal, unit_type: "monthly_retainer"
        });
      }
      const payload = {
        project_name: projectName,
        client_name: clientName,
        salesperson: user?.name || "",
        quote_type: "collateral",
        sections: [{
          id: crypto.randomUUID(),
          section_type: "marketing_collateral",
          label: "Marketing Collateral Suite",
          line_items: lineItems,
          subtotal: calc.grandTotal,
          metadata: { description: "Branding, digital presence, sales collateral, and AI-enhanced marketing content." }
        }],
        metadata: {
          collateral: {
            infra_toggles: infraToggles,
            billboard_option: billboardOption,
            ai_retainer_tier: aiRetainerTier,
            ai_enhance_qty: aiEnhanceQty,
            retainer_months: retainerMonths,
          }
        },
        total_one_time: calc.oneTimeTotal,
        total_recurring: calc.aiRetainerMonthly,
        grand_total: calc.grandTotal,
      };
      if (existingQuoteId && !isNew) {
        await apiClient.put(`/calculators/quotes/${existingQuoteId}`, payload);
        toast.success("Quote updated");
      } else {
        const res = await apiClient.post("/calculators/quotes", payload);
        toast.success("Quote saved");
        navigate(`/sales/calculators/collateral/${res.data.id}`, { replace: true });
      }
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  async function handleGenerateProposal() {
    const qid = existingQuoteId || quoteId;
    if (!qid || qid === "new") {
      toast.error("Save the quote first to generate a proposal");
      return;
    }
    setGeneratingProposal(true);
    try {
      const res = await apiClient.post(`/calculators/quotes/${qid}/generate-proposal`, { format: "html" });
      setProposalHtml(res.data.html);
      toast.success("Proposal generated");
    } catch {
      toast.error("Failed to generate proposal");
    } finally {
      setGeneratingProposal(false);
    }
  }

  function handlePrintProposal() {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(proposalHtml);
    printWindow.document.close();
    printWindow.print();
  }

  const modeLabel = "Marketing Collateral Suite";

  return (
    <div className="min-h-screen bg-[#000000] text-white">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-[#000000]/90 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/sales/calculators")} className="flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors" data-testid="back-to-calculators">
            <ChevronLeft className="w-4 h-4" /> Calculators
          </button>
          <div className="flex items-center gap-3">
            <input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="Project Name" className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-white/20 w-48" data-testid="project-name-input" />
            <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Client" className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-white/20 w-36" data-testid="client-name-input" />
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-white text-black rounded-lg px-5 py-2 text-sm font-semibold hover:bg-white/90 disabled:opacity-50 transition-all" data-testid="save-quote-btn">
              <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-10">
        <div className="mb-10">
          <p className="text-white/40 text-sm font-medium tracking-wider uppercase mb-2">Marketing Collateral Suite</p>
          <h1 className="text-4xl font-light tracking-tight">Collateral & Content Pricing</h1>
        </div>

        <div className="grid grid-cols-12 gap-12">
          {/* Left Column — Services */}
          <div className="col-span-7 space-y-8" data-testid="collateral-services">
            {infraCategories.map(cat => (
              <div key={cat.id}>
                <p className={`text-[10px] font-bold tracking-wider uppercase mb-4 ${
                  cat.color === "sky" ? "text-sky-400/60" :
                  cat.color === "violet" ? "text-violet-400/60" :
                  cat.color === "amber" ? "text-amber-400/60" : "text-rose-400/60"
                }`}>{cat.label}</p>
                <div className="space-y-1">
                  {cat.items.map(item => {
                    const Icon = item.icon;
                    const isEnabled = item.type === "dropdown" ? billboardOption !== "none"
                      : item.type === "retainer" ? aiRetainerTier !== "none"
                      : infraToggles[item.id];
                    const isDisabled = item.excludes && infraToggles[item.excludes];

                    return (
                      <div key={item.id} className={`${isDisabled ? "opacity-30 pointer-events-none" : ""}`}>
                        {item.type === "retainer" ? (
                          <div className="py-3">
                            <div className="flex items-center gap-3 mb-3">
                              <Icon className={`w-4 h-4 flex-shrink-0 ${isEnabled ? "text-white/60" : "text-white/20"}`} />
                              <div className="flex items-center gap-2 flex-1">
                                <span className={`text-sm ${isEnabled ? "text-white/80" : "text-white/50"}`}>{item.label}</span>
                                <span className="text-[10px] text-white/25">{item.subtext}</span>
                                <button onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === item.id ? null : item.id); }} className="text-white/20 hover:text-white/40 transition-colors">
                                  <HelpCircle className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            {activeTooltip === item.id && <p className="text-[11px] text-white/30 ml-7 mb-3 leading-relaxed">{item.desc}</p>}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 ml-7" data-testid="ai-retainer-selector">
                              {item.tiers.map(tier => (
                                <button key={tier.value} onClick={() => setAiRetainerTier(tier.value)}
                                  className={`text-center py-2.5 px-2 rounded-xl border transition-all ${
                                    aiRetainerTier === tier.value ? "border-white/30 bg-white/[0.08]" : "border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]"
                                  }`} data-testid={`ai-retainer-tier-${tier.value}`}>
                                  <div className={`text-xs font-semibold ${aiRetainerTier === tier.value ? "text-white" : "text-white/50"}`}>{tier.label}</div>
                                  {tier.monthly > 0 && <div className={`text-[10px] mt-0.5 tabular-nums ${aiRetainerTier === tier.value ? "text-white/60" : "text-white/25"}`}>{fmt(tier.monthly)}/mo</div>}
                                </button>
                              ))}
                            </div>
                            {aiRetainerTier !== "none" && (
                              <div className="ml-7 mt-3 flex items-center gap-3">
                                <span className="text-[10px] text-white/30">Retainer Term:</span>
                                <select value={retainerMonths} onChange={e => setRetainerMonths(parseInt(e.target.value))}
                                  className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-1 text-xs text-white focus:outline-none appearance-none cursor-pointer">
                                  {[3, 6, 9, 12, 18, 24].map(m => <option key={m} value={m} className="bg-[#000000] text-white">{m} months</option>)}
                                </select>
                              </div>
                            )}
                          </div>
                        ) : item.type === "enhancement_block" ? (
                          <div className="py-4 mt-2 border-t border-white/[0.04]" data-testid="advanced-render-enhancement">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2.5">
                                  <Icon className={`w-4 h-4 flex-shrink-0 ${isEnabled ? "text-white/70" : "text-white/20"}`} />
                                  <span className={`text-sm font-semibold ${isEnabled ? "text-white" : "text-white/50"}`}>{item.label}</span>
                                  <span className="text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full border border-rose-500/20 text-rose-300/70">Marketing Optimization Ready</span>
                                </div>
                                <p className="text-[11px] text-white/30 mt-1.5 ml-[26px] leading-relaxed tracking-wide">{item.subtitle}</p>
                              </div>
                              <div className="flex items-center gap-3 ml-4 mt-0.5">
                                <span className="text-xs text-white/30 tabular-nums">{fmt(item.price)}/image</span>
                                <Toggle enabled={infraToggles[item.id]} onChange={v => handleInfraToggle(item.id, v)} testId={`infra-toggle-${item.id}`} />
                              </div>
                            </div>
                            {infraToggles[item.id] && (
                              <div className="ml-[26px] mt-4 space-y-4">
                                <p className="text-[11px] text-white/25 leading-relaxed">{item.desc}</p>
                                <div>
                                  <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2.5">Enhancement Process</p>
                                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                                    {item.process.map((step, i) => (
                                      <div key={i} className="flex items-start gap-2">
                                        <Zap className="w-3 h-3 mt-0.5 text-rose-400/40 flex-shrink-0" />
                                        <span className="text-[11px] text-white/35 leading-snug">{step}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="flex items-center gap-4 pt-2">
                                  <div className="flex items-center gap-1">
                                    <button onClick={() => setAiEnhanceQty(Math.max(1, aiEnhanceQty - 1))} className="w-7 h-7 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center hover:bg-white/[0.1]"><Minus className="w-3 h-3 text-white/60" /></button>
                                    <span className="w-10 text-center text-sm font-semibold tabular-nums">{aiEnhanceQty}</span>
                                    <button onClick={() => setAiEnhanceQty(aiEnhanceQty + 1)} className="w-7 h-7 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center hover:bg-white/[0.1]"><Plus className="w-3 h-3 text-white/60" /></button>
                                  </div>
                                  <span className="text-[10px] text-white/25">images</span>
                                  <span className="text-sm font-semibold text-white/60 ml-auto tabular-nums">{fmt(aiEnhanceQty * item.price)}</span>
                                </div>
                                <p className="text-[10px] text-white/15 italic">{item.disclaimer}</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-between py-3">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <Icon className={`w-4 h-4 flex-shrink-0 ${isEnabled ? "text-white/60" : "text-white/20"}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm ${isEnabled ? "text-white/80" : "text-white/50"}`}>{item.label}</span>
                                  {item.subtext && <span className="text-[10px] text-white/25">{item.subtext}</span>}
                                  <button onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === item.id ? null : item.id); }} className="text-white/20 hover:text-white/40 transition-colors">
                                    <HelpCircle className="w-3 h-3" />
                                  </button>
                                </div>
                                {activeTooltip === item.id && <p className="text-[11px] text-white/30 mt-1 leading-relaxed">{item.desc}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 ml-4">
                              {item.type === "toggle" && (
                                <>
                                  <span className="text-xs text-white/30 tabular-nums">{fmt(item.price)}</span>
                                  <Toggle enabled={infraToggles[item.id]} onChange={v => handleInfraToggle(item.id, v)} testId={`infra-toggle-${item.id}`} />
                                </>
                              )}
                              {item.type === "dropdown" && (
                                <select value={billboardOption} onChange={e => setBillboardOption(e.target.value)}
                                  className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-white/20 appearance-none cursor-pointer">
                                  {item.options.map(opt => (
                                    <option key={opt.value} value={opt.value} className="bg-[#000000] text-white">
                                      {opt.label}{opt.price > 0 ? ` — ${fmt(opt.price)}` : ""}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Right Column — Premium Summary */}
          <div className="col-span-5">
            <div className="sticky top-24">
              <div className="border border-white/[0.08] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] bg-[#000000] rounded-[24px] overflow-hidden" data-testid="collateral-summary-card">
                <div className="flex items-center justify-between gap-4 px-6 pt-6 pb-4 border-b border-white/[0.06]">
                  <h2 className="text-[17px] font-light text-white tracking-tight leading-tight">Quote Summary</h2>
                  <span className="text-[10px] uppercase tracking-[0.12em] text-white/35">Marketing</span>
                </div>

                <div className="px-6 pt-6 pb-8">
                  <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-2">Total Investment</p>
                  <p className="text-[40px] font-light tracking-tight text-white leading-none mb-8 tabular-nums" data-testid="collateral-grand-total">
                    {fmt(calc.grandTotal)}
                  </p>

                  <div>
                    {calc.items.map((item, i) => (
                      <SummaryRow key={i} label={item.label} value={fmt(item.amount)} testId={`summary-item-${i}`} />
                    ))}

                    {calc.aiRetainerMonthly > 0 && (
                      <>
                        <div className="h-px bg-white/[0.06] my-2" />
                        <SummaryRow label={`AI Content (${aiRetainerTier} clips/mo × ${retainerMonths}mo)`} value={fmt(calc.aiRetainerTotal)} testId="summary-ai-retainer" />
                        <SummaryRow label="Monthly Retainer" value={`${fmt(calc.aiRetainerMonthly)}/mo`} testId="summary-ai-monthly" />
                      </>
                    )}

                    {calc.oneTimeTotal > 0 && calc.aiRetainerMonthly > 0 && (
                      <>
                        <div className="h-px bg-white/[0.06] my-2" />
                        <SummaryRow label="One-Time Services" value={fmt(calc.oneTimeTotal)} testId="summary-onetime" highlight />
                        <SummaryRow label="Recurring Total" value={fmt(calc.aiRetainerTotal)} testId="summary-recurring" highlight />
                      </>
                    )}
                  </div>

                  {/* Grand Total — premium block */}
                  <div className="mt-5 pt-5 border-t border-white/[0.08]">
                    <div className="p-4 bg-[#000000] border border-white/[0.08] rounded-[16px]">
                      <div className="flex items-baseline justify-between">
                        <span className="text-[11px] uppercase tracking-[0.1em] text-white/50">Grand Total</span>
                        <span className="text-[22px] font-light text-white tabular-nums tracking-tight" data-testid="summary-grand-total">{fmt(calc.grandTotal)}</span>
                      </div>
                    </div>
                  </div>

                  <p className="mt-6 text-[11px] text-white/30 leading-relaxed">
                    All services are one-time unless noted as monthly retainer. Prices subject to change.
                  </p>

                  <button
                    onClick={handleGenerateProposal}
                    disabled={generatingProposal || calc.grandTotal === 0}
                    className="w-full mt-5 flex items-center justify-center gap-2 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] text-white rounded-[12px] h-10 text-[13px] font-medium disabled:opacity-30 transition-all duration-200"
                    data-testid="generate-proposal-btn"
                  >
                    <FileText className="w-4 h-4" />
                    {generatingProposal ? "Generating..." : "Generate Proposal"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Proposal Preview Modal */}
      {proposalHtml && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#000000] rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col border border-white/10">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white">Generated Proposal</h2>
              <div className="flex items-center gap-2">
                <button onClick={handlePrintProposal} className="flex items-center gap-2 bg-white/[0.08] hover:bg-white/[0.12] border border-white/10 text-white rounded-lg px-4 py-2 text-sm font-medium transition-all">
                  <Printer className="w-4 h-4" /> Print / Save PDF
                </button>
                <button onClick={() => setProposalHtml(null)} className="p-2 rounded-lg hover:bg-white/[0.08] text-white/60 hover:text-white transition-all">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe srcDoc={proposalHtml} className="w-full h-full min-h-[600px] bg-white" title="Proposal Preview" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
